import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const FEATURE_LABELS = {
  project_list: "Project list",
  milestones: "Milestones",
  raid_client: "RAID (client-visible)",
  review_items: "Review items",
  action_items: "Action items",
  comments: "Comments",
  attachments: "Attachments",
  invoices: "Invoices",
};

const ROLE_LABELS = {
  client_admin: "Client admin",
  client_reviewer: "Client reviewer",
  client_viewer: "Client viewer",
};

const ROLE_HINTS = {
  client_admin: "Full portal access — owners on the client side",
  client_reviewer: "Day-to-day stakeholders who review work",
  client_viewer: "Read-only access for execs and observers",
};

export default function PortalVisibilitySection() {
  const { activeWorkspaceId } = useAuth();
  const [features, setFeatures] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [original, setOriginal] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/portal-visibility`);
      setFeatures(data.features || []);
      setMatrix(data.matrix || {});
      setOriginal(JSON.parse(JSON.stringify(data.matrix || {})));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const toggle = (role, feature) => {
    setMatrix((m) => ({ ...m, [role]: { ...(m[role] || {}), [feature]: !m[role]?.[feature] } }));
  };

  const dirty = JSON.stringify(matrix) !== JSON.stringify(original);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/portal-visibility`, { matrix });
      setMatrix(data.matrix);
      setOriginal(JSON.parse(JSON.stringify(data.matrix)));
      toast.success("Portal visibility updated");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const roles = Object.keys(matrix);
  if (roles.length === 0) return null;

  return (
    <section className="space-y-4" data-testid="portal-visibility-section">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Client portal visibility</div>
          <h2 className="text-2xl font-bold mt-1" style={{ fontFamily: "var(--font-heading)" }}>Per-role access matrix</h2>
          <p className="text-xs text-muted-foreground max-w-2xl mt-1">
            Configure exactly what each portal role can see. Changes take effect immediately for new portal sessions.
            Project-level role overrides can grant additional access but cannot reduce below the workspace default.
          </p>
        </div>
        <Button onClick={save} disabled={busy || !dirty} className="rounded-none" data-testid="portal-visibility-save">
          {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <div className="border border-border overflow-x-auto" data-testid="portal-visibility-matrix">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="label-eyebrow text-[10px] text-left px-4 py-2 border-b border-border">Feature</th>
              {roles.map((r) => (
                <th key={r} className="label-eyebrow text-[10px] text-left px-4 py-2 border-b border-border min-w-[160px]">
                  <div className="text-foreground font-bold">{ROLE_LABELS[r] || r}</div>
                  <div className="text-[10px] text-muted-foreground font-normal normal-case mt-0.5">{ROLE_HINTS[r] || ""}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map((f) => (
              <tr key={f} className="border-b border-border" data-testid={`portal-row-${f}`}>
                <td className="px-4 py-3 font-medium">{FEATURE_LABELS[f] || f}</td>
                {roles.map((r) => (
                  <td key={r} className="px-4 py-3">
                    <Switch
                      checked={!!matrix[r]?.[f]}
                      onCheckedChange={() => toggle(r, f)}
                      data-testid={`portal-toggle-${r}-${f}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
